import {
  PublicClientApplication,
  InteractionRequiredAuthError,
  BrowserAuthError,
  BrowserAuthErrorCodes,
  CacheLookupPolicy,
} from '@azure/msal-browser';
import onlineService from './onlineService';

const scopes = [`${process.env.VUE_APP_AZUREAD_CLIENT_ID}/.default`];

const msal = new PublicClientApplication({
  auth: {
    clientId: process.env.VUE_APP_AZUREAD_CLIENT_ID,
    authority: `https://login.microsoftonline.com/${process.env.VUE_APP_AZUREAD_TENANT_ID}`,
    redirectUri: process.env.VUE_APP_AZUREAD_REDIRECT_URI,
    navigateToLoginRequestUrl: false,
  },
  cache: {
    cacheLocation: 'localStorage',
  },
});

const initializePromise = msal.initialize();

function getAccount() {
  const activeAccount = msal.getActiveAccount();
  if (activeAccount) {
    return activeAccount;
  }

  const accounts = msal.getAllAccounts();
  if (accounts.length > 0) {
    msal.setActiveAccount(accounts[0]);
    return accounts[0];
  }

  return null;
}

const RENEW_BEFORE_EXPIRY_MS = 60 * 1000;

let interactiveRedirectStarted = false;
let pendingAcquire = null;
let pendingAcquireForce = false;

function isInteractiveAuthInProgress() {
  return interactiveRedirectStarted;
}

function isMockAccessToken(accessToken) {
  if (!accessToken) {
    return false;
  }

  if (
    typeof window !== 'undefined' &&
    accessToken === window.MOCK_ACCESS_TOKEN
  ) {
    return true;
  }

  return (
    !!process.env.VUE_APP_MOCK_ACCESS_TOKEN &&
    accessToken === process.env.VUE_APP_MOCK_ACCESS_TOKEN
  );
}

function accessTokenExpiryMs(accessToken) {
  if (!accessToken || typeof accessToken !== 'string') {
    return 0;
  }

  const payload = accessToken.split('.')[1];
  if (!payload) {
    return 0;
  }

  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const decoded = JSON.parse(atob(padded));
    return typeof decoded.exp === 'number' ? decoded.exp * 1000 : 0;
  } catch (err) {
    return 0;
  }
}

function isAccessTokenExpired(accessToken) {
  if (isMockAccessToken(accessToken)) {
    return false;
  }

  const expiryMs = accessTokenExpiryMs(accessToken);
  if (!expiryMs) {
    return true;
  }

  return Date.now() >= expiryMs;
}

function shouldRenewAccessToken(accessToken) {
  if (isMockAccessToken(accessToken)) {
    return false;
  }

  const expiryMs = accessTokenExpiryMs(accessToken);
  if (!expiryMs) {
    return true;
  }

  return expiryMs - Date.now() <= RENEW_BEFORE_EXPIRY_MS;
}

function clearStuckInteraction() {
  Object.keys(sessionStorage)
    .filter((key) => key.includes('interaction.status'))
    .forEach((key) => sessionStorage.removeItem(key));
}

function rememberReturnPath() {
  if (typeof window === 'undefined') {
    return;
  }

  const path = `${window.location.pathname}${window.location.search}`;
  if (!path || /signin|callback/.test(path)) {
    return;
  }

  if (!localStorage.getItem('path')) {
    localStorage.setItem('path', path);
  }
}

async function loginRedirect() {
  console.log('function loginRedirect');
  await initializePromise;

  try {
    await msal.loginRedirect({ scopes });
  } catch (err) {
    if (
      err instanceof BrowserAuthError &&
      err.errorCode === BrowserAuthErrorCodes.interactionInProgress
    ) {
      clearStuckInteraction();
      await msal.loginRedirect({ scopes });
    } else {
      throw err;
    }
  }
}

async function login() {
  if (!(await onlineService.getStatus())) {
    console.log('No internet connection');
    return null;
  }

  await initializePromise;

  const account = getAccount();
  if (account) {
    return account;
  }

  await loginRedirect();
  return null;
}

async function logoutRedirect() {
  console.log('function logoutRedirect');
  await initializePromise;
  return msal.logoutRedirect();
}

async function logout() {
  await initializePromise;
  await logoutRedirect();
}

async function handleRedirect() {
  console.log('handleRedirect');

  await initializePromise;
  const response = await msal.handleRedirectPromise();

  if (response?.account) {
    msal.setActiveAccount(response.account);
    return response.account;
  }

  return getAccount();
}

async function requestSilentToken(account, forceRefresh) {
  const tokenOptions = {
    account,
    scopes,
  };

  if (forceRefresh) {
    tokenOptions.forceRefresh = true;
    tokenOptions.cacheLookupPolicy = CacheLookupPolicy.RefreshTokenAndNetwork;
  }

  const result = await msal.acquireTokenSilent(tokenOptions);

  if (result?.account) {
    msal.setActiveAccount(result.account);
  }

  sessionStorage.setItem('accessToken', result.accessToken);
  return result.accessToken;
}

async function beginInteractiveRedirect(account) {
  if (interactiveRedirectStarted) {
    return;
  }

  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return;
  }

  interactiveRedirectStarted = true;
  rememberReturnPath();

  try {
    await msal.acquireTokenRedirect({
      account,
      scopes,
    });
  } catch (err) {
    if (
      err instanceof BrowserAuthError &&
      err.errorCode === BrowserAuthErrorCodes.interactionInProgress
    ) {
      clearStuckInteraction();
      try {
        await msal.acquireTokenRedirect({
          account,
          scopes,
        });
        return;
      } catch (retryErr) {
        interactiveRedirectStarted = false;
        throw retryErr;
      }
    }

    interactiveRedirectStarted = false;
    throw err;
  }
}

async function acquireTokenUncached(forceRefresh) {
  await initializePromise;

  const account = getAccount();
  if (!account) {
    return null;
  }

  try {
    let accessToken = await requestSilentToken(account, forceRefresh);

    if (shouldRenewAccessToken(accessToken) && !forceRefresh) {
      try {
        const renewed = await requestSilentToken(account, true);
        if (renewed && !isAccessTokenExpired(renewed)) {
          accessToken = renewed;
        }
      } catch (renewErr) {
        if (isAccessTokenExpired(accessToken)) {
          throw renewErr;
        }
      }
    }

    if (isAccessTokenExpired(accessToken)) {
      await beginInteractiveRedirect(account);
      return null;
    }

    return accessToken;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      await beginInteractiveRedirect(account);
      return null;
    }

    if (
      err instanceof BrowserAuthError &&
      err.errorCode === BrowserAuthErrorCodes.interactionInProgress
    ) {
      clearStuckInteraction();
      try {
        const retried = await requestSilentToken(account, true);
        if (!isAccessTokenExpired(retried)) {
          return retried;
        }
      } catch (retryErr) {
        if (
          retryErr instanceof BrowserAuthError &&
          retryErr.errorCode === BrowserAuthErrorCodes.interactionInProgress
        ) {
          interactiveRedirectStarted = true;
          return null;
        }
        if (!(retryErr instanceof InteractionRequiredAuthError)) {
          throw retryErr;
        }
      }

      await beginInteractiveRedirect(account);
      return null;
    }

    throw err;
  }
}

async function acquireToken(forceRefresh = false) {
  if (window.MOCK_ACCESS_TOKEN) {
    return window.MOCK_ACCESS_TOKEN;
  }

  if (process.env.VUE_APP_MOCK_ACCESS_TOKEN) {
    return process.env.VUE_APP_MOCK_ACCESS_TOKEN;
  }

  if (interactiveRedirectStarted) {
    return null;
  }

  if (pendingAcquire && (!forceRefresh || pendingAcquireForce)) {
    return pendingAcquire;
  }

  pendingAcquireForce = !!forceRefresh;
  const run = acquireTokenUncached(forceRefresh).finally(() => {
    if (pendingAcquire === run) {
      pendingAcquire = null;
      pendingAcquireForce = false;
    }
  });
  pendingAcquire = run;
  return run;
}

export {
  login,
  logout,
  handleRedirect,
  acquireToken,
  loginRedirect,
  isAccessTokenExpired,
  isInteractiveAuthInProgress,
};
